import type { UnavailableReason } from "_shared/_api/by-ids/by-ids-result";
import type { RootState } from "_shared/_stores/root-reducer";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import type { UserInformationJSON } from "users/information/_schemas/user-information-schema";
import UserInformation from "users/information/user-information";
import { requestNetworkConnectionStatus } from "users/networking/connections/_hooks/use-network-connection-status";

export type SelectedNetworkConnection =
  | { userId: Guid; state: "found"; name: string; connection: UserInformation }
  | { userId: Guid; state: "loading"; name: string }
  | { userId: Guid; state: "unavailable"; name: string; reason: UnavailableReason };

/** What a picker shows for a selected person it cannot show by name. */
export const SELECTED_CONNECTION_LABELS = {
  loading: "Loading…",
  missing: "No longer in your network",
  unauthorized: "Not visible to you",
} as const;

const selectEntries = (state: RootState) => state.networkConnections.byUserId.entries;
const selectStatuses = (state: RootState) => state.networkConnections.statusByUserId.entries;
const selectUnavailable = (state: RootState) => state.networkConnections.unavailableByUserId.entries;

/**
 * Names the selected people of a picker, including the ones that are not on a page it has loaded.
 * A person this session has not loaded is asked for in one bulk request with the other unknown ids
 * (`useNetworkConnectionStatus`). An id that the server does not return is shown as unavailable,
 * with its reason, never dropped: "missing" (no connection) and "unauthorized" (refused) read
 * differently.
 */
export default function useSelectedNetworkConnections(userIds: readonly Guid[]): SelectedNetworkConnection[] {
  const dispatch = useAttorneyHubDispatch();
  const entries = useSelector(selectEntries);
  const statuses = useSelector(selectStatuses);
  const unavailable = useSelector(selectUnavailable);

  const selected = useMemo(
    () => userIds.map((userId) => describe(userId, entries[userId.value], statuses[userId.value], unavailable[userId.value])),
    [entries, statuses, unavailable, userIds],
  );

  useEffect(() => {
    for (const person of selected) {
      if (person.state === "loading") requestNetworkConnectionStatus(dispatch, person.userId);
    }
  }, [dispatch, selected]);

  return selected;
}

function describe(
  userId: Guid,
  entry: UserInformationJSON | undefined,
  status: boolean | undefined,
  reason: UnavailableReason | undefined,
): SelectedNetworkConnection {
  const connection = UserInformation.fromJSON(entry);
  if (connection) return { userId, state: "found", name: connection.name.fullName, connection };
  if (reason) return { userId, state: "unavailable", name: SELECTED_CONNECTION_LABELS[reason], reason };
  if (status === false) {
    return { userId, state: "unavailable", name: SELECTED_CONNECTION_LABELS.missing, reason: "missing" };
  }
  return { userId, state: "loading", name: SELECTED_CONNECTION_LABELS.loading };
}
